import { types, Immutable, Mutable } from "../../helpers/Util";
import {
  DatabaseTable,
  returnID,
  getTableRow,
  getAllRowsForTable,
} from "../BaseTable";
import { List } from "immutable";

export enum enum_conditional {
  and = "and",
  or = "or",
  anchor_property = "anchor_property",
  survey_instance = "survey_instance",
  survey_answer = "survey_answer",
  v1_data = "v1_data",
  condition = "condition",
  else = "else",
}

export enum enum_operator {
  eq = "=",
  ls = "<",
  lseq = "<=",
  gr = ">",
  greq = ">=",
}

export const convertOperator = (op: string): enum_operator => {
  if (op === "=") {
    return enum_operator.eq;
  } else if (op === "<") {
    return enum_operator.ls;
  } else if (op === "<=") {
    return enum_operator.lseq;
  } else if (op === ">") {
    return enum_operator.gr;
  } else if (op === ">=") {
    return enum_operator.greq;
  } else {
    throw new Error("Operator is invalid");
  }
};

class ConditionRaw extends DatabaseTable {
  readonly id: number;
  readonly set_condition_id : number; // The id of the root condition for a set of conditions, conditions can be organized into sets for the purposes of useability and efficient querying
  readonly parent_condition_id : number | null; // The id of the parent condition, conditions can have deep structures, this is the immediate parent
  readonly name: string | null; // A string useful for identifying condition sets
  readonly conditional: enum_conditional; // The type of this condition, some types are specfic for leaf nodes, others are expected to have children, see the enum_conditional type. TODO - should this be nested obj?
  readonly from: string | null; // Used for pulling V1 data. This is the Table that data is being pulled from.
  readonly as: string | null; // Used for pulling V1 data. This allows you to specify the alias of the table, allowing you to check for multiple rows of a single table
  readonly key: string | null; // Most leaf conditons are on a key value pair, this defines the key, i.e. for a survey answer condition this would be the question
  readonly value: string | null; // Most leaf conditions are on a key value pair, this defines the value, i.e. for a survey answer condition this would be the answer
  readonly operator: enum_operator;
  readonly not: boolean; // Apply a logical not operation to the results of this condition
  readonly ordinal: number; // Order of how conditions should appear in the UI

  // This allows us to do typing in js
  static readonly serializableFields = new Map([
    ["id", [types.number]],
    ["set_condition_id", [types.number, types.null]],
    ["parent_condition_id", [types.number, types.null]],
    ["name", [types.string, types.null]],
    ["conditional", [types.string]],
    ["from", [types.string, types.null]],
    ["as", [types.string, types.null]],
    ["key", [types.string, types.null]],
    ["value", [types.string, types.null]],
    ["operator", [types.string]],
    ["not", [types.boolean]],
    //["ordinal", [types.number]],
  ]);
  static readonly tableName = "Condition";

  constructor(
    id = -1,
    set_condition_id = -1,
    parent_condition_id  = null,
    name = null,
    conditional = enum_conditional.and,
    from = null,
    as = null,
    key = "",
    value = "",
    operator = enum_operator.eq,
    not = false,
    ordinal = 0,
  ) {
    super();
    this.id = returnID(id);
    this.set_condition_id = set_condition_id;
    this.parent_condition_id  = parent_condition_id ;
    this.name = name;
    this.conditional = conditional;
    this.from = from;
    this.as = as;
    this.key = key;
    this.value = value;
    this.operator = operator;
    this.not = not;
    this.ordinal = ordinal;
  }
}

export default class Condition extends ConditionRaw {
  children: List<number> = List(); // List of Condition id's that are a child of this Condition
  conditionsToDelete: List<number> = List(); // List of conditions that were removed from this Condition, and should be deleted when Condition is saved or deleted

  constructor(...args: any[]) {
    super(...args);
  }

  protected initialize() {
    // If set is -1 or null, then that means this Condition is a root Condition
    if (this.set_condition_id === -1 || this.set_condition_id === null) {
      (this as any).set_condition_id = this.id;
    }

    // Go through conditions to find children and parent of condition
    const conditions = getAllRowsForTable(Condition);
    const children: number[] = [];
    conditions?.forEach((condition) => {
      if (condition.parent_condition_id  === this.id) {
        children.push(condition.id);
      } else if (this.parent_condition_id  === condition.id) {
        condition.addChild(this, true);
      }
    });

    this.children = List(children);

    return super.initialize();
  }

  // If I'm saving a new root condition, I need to make sure set is also null
  // That way when it gets inserted, it knows to make set the same as id
  protected beforeSave(): this {
    const copy = { ...this } as any;
    if (copy.set_condition_id < 0) {
      if (copy.id === copy.set_condition_id) {
        copy.set_condition_id = null;
      } else {
        throw new Error(
          "Trying to save condition to a set that doesn't exist yet.",
        );
      }
    }
    return copy as typeof this;
  }

  // Conditions need to tell their children to save
  // Also need to delete any children that were removed
  protected async afterSave(oldRecord?: Condition) {
    // If Condition was inserted, then oldRecord will be set
    // In that case, we should use the old children and conditionsToDelete

    // If oldRecord is not set, then that means we just updated the db with our data
    // In that case, this Condition has the correct children and conditionsToDelete
    let children: List<number>;
    let conditionsToDelete: List<number>;
    if (typeof oldRecord === "undefined") {
      children = this.children;
      conditionsToDelete = this.conditionsToDelete;
    } else {
      children = oldRecord.children;
      conditionsToDelete = oldRecord.conditionsToDelete;
    }
    // Reset children, in case children are inserted and new records get made
    this.children = List();
    // Save each child of this Condition
    let index = 0;
    for (const childID of children.toArray()) {
      const child = getTableRow(Condition, childID) as
        | Mutable<Condition>
        | undefined;
      if (typeof child === "undefined") {
        throw new Error("Unable to find child");
      }
      // Make sure these fields are set correctly before saving
      child.ordinal = index;
      child.set_condition_id = this.set_condition_id;
      child.parent_condition_id  = this.id;
      const newChild = await child.save();
      this.children = this.children.push(newChild.id);
      index++;
    }
    // Delete any conditions that were removed
    conditionsToDelete.forEach((conditionID) => {
      getTableRow(Condition, conditionID)?.delete();
    });
  }

  // If Condition is deleted, then children also should be deleted
  // Also need to make sure removed children are deleted
  protected onDelete() {
    this.children.forEach((childID: number) => {
      getTableRow(Condition, childID)?.delete();
    });
    this.conditionsToDelete.forEach((conditionID) => {
      getTableRow(Condition, conditionID)?.delete();
    });
  }

  // Adds a child to this Condition, after child has already been created
  // Optionally makes sure the Condition isn't already a child
  // Will update child as well if parent is not equal to Condition's id
  // That update could force unnecesary rerenders, so in general try to set parent ahead of time!
  public addChild(child: Condition, checkExists = false): Immutable<Condition> {
    if (
      checkExists &&
      typeof this.children.find((childID) => child.id === childID) !==
        "undefined"
    ) {
      // child condition is already a child of this condition, so do nothing
      return this;
    }
    if (child.parent_condition_id  !== this.id) {
      child.update("parent_condition_id", this.id);
    }
    return this.update("children", this.children.push(child.id));
  }

  // Removes a child from this Condition
  // Saves id of child to delete later if Condition is saved or deleted
  public removeChild(child: Condition): Immutable<Condition> {
    const index = this.children.findIndex((childID) => child.id === childID);
    if (index > -1) {
      if (child.id > -1) {
        this.conditionsToDelete = this.conditionsToDelete.push(child.id);
      }
      child.update("parent_condition_id", null);
      return this.update("children", this.children.delete(index));
    } else {
      return this;
    }
  }

  // Moves a condition in a parent condition's children array
  // If oldParentID is passed in, it will also remove the condition from that condition's children array
  public static moveCondition(
    conditionID: number,
    parentID: number,
    index: number,
    oldParentID?: number,
  ) {
    // Get all the Conditions from the passed in ID's
    const condition = getTableRow(Condition, conditionID);
    const parent = getTableRow(Condition, parentID);
    const oldParent = getTableRow(Condition, oldParentID as number);

    // If either condition or parent can't be found, throw error because this function will fail
    if (typeof condition === "undefined" || typeof parent === "undefined") {
      throw new Error(
        "Can't find condition " + conditionID + " or parent " + parentID,
      );
    }

    let children = parent.children;
    // Check if the condition is already a child of parent; if so, remove it
    const oldIndex = children.findIndex((childID) => childID === conditionID);
    if (oldIndex > -1) {
      children = children.splice(oldIndex, 1);
    }
    // Add condition to parent at the specified index
    children = children.splice(index, 0, conditionID);

    // Check if oldParent was passed in, and if it was, remove condition from old parent
    let oldParentChildren = oldParent?.children;
    const oldParentIndex = oldParentChildren?.findIndex(
      (childID) => childID === conditionID,
    );
    if (typeof oldParentIndex !== "undefined" && oldParentIndex > -1) {
      oldParentChildren = oldParentChildren?.splice(oldParentIndex, 1);
    }

    // Send all our updates to redux
    parent.update("children", children);
    condition.update("parent_condition_id", parentID);
    if (
      typeof oldParent !== "undefined" &&
      typeof oldParentChildren !== "undefined"
    ) {
      oldParent.update("children", oldParentChildren);
    }
  }

  // Converts a condition into a string for readability
  public toString() {
    let conditionString = "";
    if (
      (this.conditional === enum_conditional.and ||
        this.conditional === enum_conditional.or) &&
      this.children.size > 0
    ) {
      conditionString += this.not ? "not " : "";
      conditionString += "(";
      conditionString += this.children.reduce(
        (accumulator: string, childID: number, index: number) => {
          const child = getTableRow(Condition, childID);
          accumulator = accumulator + child?.toString();
          if (index < this.children.size - 1) {
            accumulator += " " + this.conditional + " ";
          }
          return accumulator;
        },
        "",
      );
      conditionString += ")";
    } else if (this.conditional === enum_conditional.anchor_property) {
      conditionString += "anchor." + this.key + " ";
      conditionString += this.not ? "!" : "";
      conditionString += "= '" + this.value + "'";
    } else if (this.conditional === enum_conditional.v1_data) {
      conditionString += this.not ? "not " : "";
      conditionString +=
        "exists(select 1 from `CareSenseData`.`" + this.from + "`";
      if (this.as !== null) {
        conditionString +=
          " as `" +
          this.as +
          "` where `" +
          this.as +
          "`.`" +
          this.key +
          "` = `" +
          this.value +
          "`";
      } else {
        conditionString +=
          "` where `" +
          this.from +
          "`.`" +
          this.key +
          "` = `" +
          this.value +
          "`";
      }
      conditionString += ")";
    } else if (this.conditional === enum_conditional.else) {
      conditionString += "(conditions on previous siblings are false)";
    } else if (this.conditional === enum_conditional.condition) {
      conditionString += "(" + this.name + " is true)";
    } else if (this.conditional === enum_conditional.survey_instance) {
      conditionString += this.not ? "not " : "";
      conditionString +=
        "(survey_instance.type = '" +
        this.key +
        "' and survey_instance.value = '" +
        this.value +
        "')";
    } else if (this.conditional === enum_conditional.survey_answer) {
      conditionString += this.not ? "not " : "";
      conditionString +=
        "(survey_answer.question = '" +
        this.key +
        "' and survey_answer.answer = '" +
        this.value +
        "')";
    }
    return conditionString;
  }
}
